import {
  flat,
  isTombstone,
  isUntitled,
  parseEntity,
  picksIn,
  sectionNamed,
  sections,
  transcriptTopics,
} from '../entity/entity';
import { durationInNote, rowsPathFor } from './duration';

/**
 * A capture, reduced to what a row shows.
 *
 * The face is the recognition device — the few picked memory triggers, which
 * is what brings a recording back at a glance. A third of the corpus has
 * none, so the first line of the body stands in; it is the raw words rather
 * than the point, and the row says so rather than passing it off as a face.
 */

export interface Thought {
  path: string;
  /** A name someone gave it, or null when the filename is only a timestamp. */
  title: string | null;
  face: string[];
  standIn: string;
  topics: number;
  /** A deletion marker: a real file under the original capture's name, with
   *  an empty body. Only the file says so — the manifest cannot. */
  tombstone: boolean;
  /** How long its audio runs, where that is known. */
  durationMs: number | null;
  /** The rows sidecar that says how long it runs, where the note itself does
   *  not. */
  rowsAt: string | null;
}

/** A path that turned out to be a deletion marker, or to resolve to nothing. */
export function tombstoneAt(path: string): Thought {
  return { path, title: null, face: [], standIn: '', topics: 0, tombstone: true, durationMs: null, rowsAt: null };
}

export function readThought(path: string, raw: string): Thought {
  const entity = parseEntity(path, raw);
  if (isTombstone(entity)) return tombstoneAt(path);
  const durationMs = durationInNote(raw);
  const id = flat(entity.frontMatter['id']).trim();

  return {
    path,
    title: isUntitled(entity) ? null : entity.title,
    face: picksIn(sectionNamed(entity, 'face')?.body ?? '')
      .map((p) => p.text)
      .filter(isSpoken),
    standIn: standInFor(entity),
    topics: sections(entity).length,
    tombstone: false,
    durationMs,
    rowsAt: durationMs === null && id && raw.includes('<!--pondering:unit ') ? rowsPathFor(id) : null,
  };
}

/**
 * Exports up to 2026-05 listed the recording's AUDIO block among the picks,
 * and its text is the message payload — `{"uri":…,"durationMs":…}`. 1,402
 * files in the vault carry one, so an unfiltered face opens a third of the
 * corpus with a JSON blob where the first spoken line should be.
 */
function isSpoken(text: string): boolean {
  return !text.trimStart().startsWith('{"');
}

function standInFor(entity: ReturnType<typeof parseEntity>): string {
  const spoken = transcriptTopics(entity)[0]?.paragraphs[0];
  if (spoken) return spoken;

  // A typed note has no transcript, so the body itself is the words. Headings
  // are the export's own structure and never stand in for what was said.
  return (
    entity.body
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line !== '' && !line.startsWith('#') && !line.startsWith('<!--')) ?? ''
  );
}
